<template>
  <CModal :on-esc="close" show :z-index="zIndex">
    <div class="flex flex-col pa-4 gap-6 rounded-4 self-center w-full max-w-[640px] bg-white">
      <header class="text-5 leading-8 tracking-[0.25px]">
        {{ t('canvasUI.agendaSelector.moveItemModal.title') }}
      </header>

      <main class="flex flex-col gap-2">
        <CSearch
          v-model="search"
          :placeholder="t('canvasUI.agendaSelector.moveItemModal.searchPlaceholder')"
          show-icon
        />

        <div v-if="visibleGroups.length" class="flex flex-col gap-2">
          <NCheckbox
            v-for="group in visibleGroups"
            :key="group.id"
            :checked="toGroup === group.id"
            class="p-3 font-size-4 rounded-1 border-1 border-solid border-primary5"
            :label="`${group.name} (${group.items.length})`"
            @update:checked="onChecked($event, group.id)"
          />
        </div>

        <div v-else class="pa-4 color-text3 bg-primary6 font-size-4 rounded-1 text-center">
          {{ t('canvasUI.agendaSelector.moveItemModal.noResults') }}
        </div>
      </main>

      <footer class="flex justify-between">
        <CButton @click="close">
          {{ t('canvasUI.common.cancel') }}
        </CButton>

        <CButton :disabled="!toGroup" type="primary" @click="submit()">
          {{ t('canvasUI.agendaSelector.moveItemModal.submit') }}
        </CButton>
      </footer>
    </div>
  </CModal>
</template>

<script lang="ts" setup>
import { ref, computed, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { NCheckbox } from 'naive-ui'
import CSearch from '@lib/components/CSearch/CSearch.vue'
import CModal from '@lib/components/CModal/CModal.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import type { AgendaGroup, AgendaItem } from '@agenda-selector/types/agenda.type'
import { useMoveItemModal } from '@agenda-selector/composables/move-item-modal.use'

const { t } = useI18n()

const emit = defineEmits<{
  close: []
  moveToGroup: [fromGroup: string, toGroup: string, item: AgendaItem]
}>()

const props = defineProps<{
  zIndex?: number
  groups: AgendaGroup[]
}>()

const { close: closeMoveItemModal, activeElement } = useMoveItemModal()

const toGroup = ref<string | null>(null)
const search = ref<string>('')

const visibleGroups = computed(() => {
  const normalizedSearch = search.value.trim().toLowerCase()

  return props.groups.filter((_group) => {
    const isNotCurrentGroup = activeElement.value?.groupId !== _group.id
    const containsSearchPhrase = _group.name.toLowerCase().includes(normalizedSearch)

    return isNotCurrentGroup && containsSearchPhrase
  })
})

const onChecked = (checked: boolean, groupId: string) => {
  toGroup.value = checked ? groupId : null
}

const submit = () => {
  if (!toGroup.value || !activeElement.value) {
    console.error('No group selected')
    return
  }

  emit('moveToGroup', activeElement.value.groupId, toGroup.value, activeElement.value.item)

  closeMoveItemModal()
}

const close = () => {
  emit('close')

  closeMoveItemModal()
}

watchEffect(() => {
  if (toGroup.value && !visibleGroups.value.find((group) => group.id === toGroup.value)) {
    toGroup.value = null
  }
})
</script>
